/**
 * Night Sky Dashboard – API Service
 * ══════════════════════════════════
 * Fetches live data from the FastAPI backend.
 * Falls back to hardcoded defaults if backend is unreachable.
 */

const API_BASE =
  process.env.REACT_APP_API_URL ||
  "https://nightsky-api-341473370515.us-central1.run.app";

/**
 * Fetch all dashboard data in a single call.
 * This is the primary endpoint used on initial load.
 */
export async function fetchDashboardData() {
  try {
    const resp = await fetch(`${API_BASE}/api/dashboard`, {
      signal: AbortSignal.timeout(8000),
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    return await resp.json();
  } catch (err) {
    console.warn("⚠️ API unreachable, using fallback data:", err.message);
    return null;
  }
}

/**
 * Fetch individual endpoints (for periodic refresh)
 */
export async function fetchNEO() {
  const resp = await fetch(`${API_BASE}/api/neo?limit=50`);
  return resp.json();
}

export async function fetchWeather() {
  const resp = await fetch(`${API_BASE}/api/weather`);
  return resp.json();
}

export async function fetchVisibility() {
  const resp = await fetch(`${API_BASE}/api/visibility`);
  return resp.json();
}

export async function fetchMoon() {
  const resp = await fetch(`${API_BASE}/api/moon`);
  return resp.json();
}

export async function fetchMeteorShowers() {
  const resp = await fetch(`${API_BASE}/api/meteor-showers`);
  return resp.json();
}

export async function fetchSatellitePositions() {
  const resp = await fetch(`${API_BASE}/api/satellites/positions?limit=50`);
  return resp.json();
}

export async function fetchHealth() {
  const resp = await fetch(`${API_BASE}/api/health`);
  return resp.json();
}

// ── Analytics endpoints (BigQuery warehouse marts) ──

export async function fetchCityRankings(limit = 25) {
  try {
    const resp = await fetch(
      `${API_BASE}/api/analytics/city-rankings?limit=${limit}`,
      {
        signal: AbortSignal.timeout(10000),
      },
    );
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    return await resp.json();
  } catch (err) {
    console.warn("⚠️ City rankings unavailable:", err.message);
    return null;
  }
}

export async function fetchRegionalOverview() {
  try {
    const resp = await fetch(`${API_BASE}/api/analytics/regional-overview`, {
      signal: AbortSignal.timeout(10000),
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    return await resp.json();
  } catch (err) {
    console.warn("⚠️ Regional overview unavailable:", err.message);
    return null;
  }
}

export async function fetchWeeklyTrends(limit = 20) {
  try {
    const resp = await fetch(
      `${API_BASE}/api/analytics/weekly-trends?limit=${limit}`,
      {
        signal: AbortSignal.timeout(10000),
      },
    );
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    return await resp.json();
  } catch (err) {
    console.warn("⚠️ Weekly trends unavailable:", err.message);
    return null;
  }
}

export async function fetchDarkSkyCalendar(limit = 30) {
  try {
    const resp = await fetch(
      `${API_BASE}/api/analytics/dark-sky-calendar?limit=${limit}`,
      {
        signal: AbortSignal.timeout(10000),
      },
    );
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    return await resp.json();
  } catch (err) {
    console.warn("⚠️ Dark sky calendar unavailable:", err.message);
    return null;
  }
}

export async function fetchNeoRisk() {
  try {
    const resp = await fetch(`${API_BASE}/api/analytics/neo-risk`, {
      signal: AbortSignal.timeout(10000),
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    return await resp.json();
  } catch (err) {
    console.warn("⚠️ NEO risk unavailable:", err.message);
    return null;
  }
}
